import React, { useEffect, useState } from "react";
import { api, formatError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import {
  PageHeader, BtnPrimary, BtnSecondary, BtnGhost, Modal, Field, Input, Select,
  Textarea, Empty,
} from "@/components/primitives";
import { toast } from "sonner";
import { Plus, Trash, PencilSimple, Function, Play } from "@phosphor-icons/react";

const CATEGORIES = [
  { value: "earning",   label: "Earning",   color: "#00A142" },
  { value: "deduction", label: "Deduction", color: "#FF2A2A" },
  { value: "summary",   label: "Summary",   color: "#002FA7" },
];

const BLANK = { name: "", category: "deduction", expression: "", description: "", order: 0, active: true };

const EXAMPLE_TESTS = {
  basic: 8800, days_worked: 20, public_holiday: 2, leave_days: 28,
  allowances: 1580, gross: 10380, taxable_gross: 10380,
};

export default function PayrollFormulas() {
  return (
    <div>
      <PageHeader
        title="Payroll Formulas"
        subtitle="Define how earnings, statutory deductions, and net-pay lines are calculated."
      />
      <PayrollFormulasPanel />
    </div>
  );
}

export function PayrollFormulasPanel() {
  const { user } = useAuth();
  const canEdit = user.role === "admin" || user.role === "finance";
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState(BLANK);
  const [testResults, setTestResults] = useState(null);

  async function load() {
    try { const { data } = await api.get("/payroll-formulas"); setRows(data); }
    catch (e) { toast.error(formatError(e)); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  function openNew() { setEdit(null); setForm(BLANK); setOpen(true); }
  function openEdit(f) {
    setEdit(f); setForm({
      name: f.name, category: f.category || "deduction",
      expression: f.expression, description: f.description || "",
      order: f.order || 0, active: f.active !== false
    });
    setOpen(true);
  }

  async function save() {
    if (!form.name || !form.expression) {
      toast.error("Name and expression are required");
      return;
    }
    try {
      if (edit) await api.put(`/payroll-formulas/${edit.id}`, form);
      else await api.post("/payroll-formulas", form);
      setOpen(false); load();
      toast.success(edit ? "Formula updated" : "Formula created");
    } catch (e) { toast.error(formatError(e)); }
  }

  async function remove(f) {
    if (!window.confirm(`Delete formula "${f.name}"?`)) return;
    try { await api.delete(`/payroll-formulas/${f.id}`); load(); toast.success("Formula removed"); }
    catch (e) { toast.error(formatError(e)); }
  }

  async function runTest() {
    try {
      const { data } = await api.post("/payroll-formulas/evaluate", {
        formulas: [],
        vars: EXAMPLE_TESTS,
      });
      setTestResults(data);
    } catch (e) { toast.error(formatError(e)); }
  }

  const groups = { earning: [], deduction: [], summary: [] };
  rows.forEach((r) => (groups[r.category] || groups.deduction).push(r));

  if (loading) return <div className="phizo-label">Loading formulas…</div>;

  if (!canEdit && user.role !== "hr" && user.role !== "director") {
    return <Empty label="Payroll formulas are restricted to Admin and Finance roles." />;
  }

  return (
    <div data-testid="formulas-panel">
      <div className="flex items-center justify-between mb-4 gap-2 flex-wrap">
        <div className="flex-1 min-w-[200px]">
          <div className="phizo-label">Calculation Library</div>
          <div className="text-xs text-[#a1a1aa]">
            Formulas execute in order for every payroll row (earning → deduction → summary).
          </div>
        </div>
        <div className="flex gap-2">
          <BtnSecondary onClick={runTest} data-testid="formula-run-test">
            <Play size={12} weight="bold" /> Test with sample values
          </BtnSecondary>
          {canEdit && (
            <BtnPrimary onClick={openNew} data-testid="formula-new">
              <Plus size={12} weight="bold" /> New Formula
            </BtnPrimary>
          )}
        </div>
      </div>

      {testResults && (
        <div className="phizo-card p-4 mb-6" data-testid="formula-test-results">
          <div className="phizo-label mb-2">
            Evaluation with sample values ({Object.entries(EXAMPLE_TESTS).map(([k,v])=>`${k}=${v}`).join(", ")})
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 text-sm">
            {testResults.results.map((r, i) => (
              <div key={i} className={`border p-2 ${r.error ? "border-[#FF2A2A]" : "border-[#e4e4e7]"}`}>
                <div className="phizo-label text-[10px]">{r.category}</div>
                <div className="font-bold">{r.name}</div>
                <div className={`mono text-xs ${r.error ? "text-[#FF2A2A]" : "text-[#00A142]"}`}>
                  {r.error ? `Error: ${r.error}` : `= ${typeof r.value === "number" ? r.value.toFixed(2) : r.value}`}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {CATEGORIES.map((cat) => (
          <div key={cat.value} className="phizo-card">
            <div className="p-3 border-b border-[#e4e4e7]" style={{ borderTop: `3px solid ${cat.color}` }}>
              <div className="phizo-label" style={{ color: cat.color }}>{cat.label}s</div>
              <div className="text-xs text-[#a1a1aa]">{groups[cat.value].length} formulas</div>
            </div>
            {groups[cat.value].length === 0 ? (
              <div className="p-4"><Empty label={`No ${cat.label.toLowerCase()} formulas yet.`} /></div>
            ) : (
              <ul className="divide-y divide-[#f4f4f5]">
                {groups[cat.value].sort((a, b) => (a.order || 0) - (b.order || 0)).map((f) => (
                  <li key={f.id} className="p-3" data-testid={`formula-row-${f.name}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <Function size={14} weight="bold" className="text-[#52525b]" />
                          <div className="font-bold text-sm">{f.name}</div>
                          {!f.active && <span className="phizo-label text-[9px] text-[#FF2A2A]">Inactive</span>}
                        </div>
                        <div className="mono text-xs text-[#52525b] mt-1 break-all">
                          {f.expression}
                        </div>
                        {f.description && (
                          <div className="text-xs text-[#a1a1aa] mt-1">{f.description}</div>
                        )}
                      </div>
                      {canEdit && (
                        <div className="flex flex-col gap-1">
                          <BtnGhost onClick={() => openEdit(f)} data-testid={`formula-edit-${f.name}`}>
                            <PencilSimple size={12} weight="bold" />
                          </BtnGhost>
                          <BtnGhost onClick={() => remove(f)} className="text-[#FF2A2A]">
                            <Trash size={12} weight="bold" />
                          </BtnGhost>
                        </div>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        ))}
      </div>

      <Modal open={open} onClose={() => setOpen(false)}
        title={edit ? "Edit Formula" : "New Formula"}
        actions={
          <>
            <BtnSecondary onClick={() => setOpen(false)}>Cancel</BtnSecondary>
            <BtnPrimary onClick={save} data-testid="formula-save">Save</BtnPrimary>
          </>
        }
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="Name *">
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="formula-name" placeholder="e.g. PAYE" />
          </Field>
          <Field label="Category *">
            <Select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="formula-cat">
              {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
            </Select>
          </Field>
          <Field label="Order">
            <Input type="number" value={form.order} onChange={(e) => setForm({ ...form, order: Number(e.target.value) })} />
          </Field>
          <Field label="Active">
            <Select value={form.active ? "yes" : "no"} onChange={(e) => setForm({ ...form, active: e.target.value === "yes" })}>
              <option value="yes">Yes</option>
              <option value="no">No</option>
            </Select>
          </Field>
        </div>
        <Field label="Expression *" className="mt-3">
          <Textarea
            value={form.expression}
            onChange={(e) => setForm({ ...form, expression: e.target.value })}
            rows={3}
            className="mono text-xs"
            data-testid="formula-expression"
            placeholder="max(0, (taxable_gross - 5100) * 0.20)"
          />
        </Field>
        <Field label="Description">
          <Textarea
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            rows={2}
            data-testid="formula-desc"
          />
        </Field>
        <div className="mt-3 text-[10px] text-[#52525b] bg-[#f4f4f5] p-3">
          <div className="phizo-label mb-1">Available variables</div>
          <div className="mono">basic, hours_worked, hourly_rate, days_worked, public_holiday, leave_days, allowances, gross, taxable_gross</div>
          <div className="phizo-label mt-2 mb-1">Available functions</div>
          <div className="mono">min · max · round · abs</div>
          <div className="phizo-label mt-2 mb-1">Example — PAYE Zambia (Jan 2026 scale)</div>
          <div className="mono">0 if taxable_gross &lt; 5100 else min((taxable_gross-5100)*0.20, (taxable_gross-5100)*0.375)</div>
        </div>
      </Modal>
    </div>
  );
}
